import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * useBulkInvoiceViewPreInvoices - Hook para facturar múltiples prefacturas
 * 
 * Ejecuta POST /invoices/pre-invoices/bulk-invoice con array de IDs para marcar
 * múltiples prefacturas como facturadas (CLOSED) en una sola operación.
 * Maneja estados de loading, éxito y error con toasts apropiados.
 * 
 * @returns {Object} Función bulkInvoiceViewPreInvoices y estado isInvoicing
 */
export const useBulkInvoiceViewPreInvoices = () => {
  const { t } = useTranslation('viewPreInvoicesModule');
  const { toast } = useToast();
  const [isInvoicing, setIsInvoicing] = useState(false);

  const bulkInvoiceViewPreInvoices = async (
    preInvoiceIds: number[],
    onSuccess?: () => void
  ) => {
    setIsInvoicing(true);

    try {
      console.log('🔍 useBulkInvoiceViewPreInvoices - Facturando prefacturas:', { preInvoiceIds, count: preInvoiceIds.length });

      const httpClient = createHttpClient(apiClientWH);
      const response = await httpClient.post<any>(
        '/invoices/pre-invoices/bulk-invoice',
        { ids: preInvoiceIds }
      );

      console.log('🔍 useBulkInvoiceViewPreInvoices - Respuesta:', response);

      // 🎯 Si la API devuelve 200 OK sin la propiedad 'success', considerarlo éxito
      const isSuccess = response?.success !== false && (response?.success === true || typeof response?.success === 'undefined');
      
      if (isSuccess) {
        // 🎯 Mostrar toast de éxito con mensaje de la API
        toast({
          title: t('toast.bulk_invoice_success.title', 'Prefacturas facturadas'),
          description: response?.message || t('toast.bulk_invoice_success.description', {
            count: preInvoiceIds.length,
            defaultValue: `Se han facturado ${preInvoiceIds.length} prefacturas exitosamente`,
          }),
          variant: 'default',
        });

        // 🎯 Ejecutar callback de éxito (refetch, limpiar selección, etc.)
        onSuccess?.();
      } else {
        // 🎯 Mostrar error devuelto por la API
        toast({
          title: t('toast.bulk_invoice_error.title', 'Error al facturar'),
          description: response?.message || t('toast.bulk_invoice_error.unexpected'),
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.log('🔍 useBulkInvoiceViewPreInvoices - Error:', error);

      // 🎯 Mostrar error inesperado
      toast({
        title: t('toast.bulk_invoice_error.title', 'Error al facturar'),
        description: error?.message || t('toast.bulk_invoice_error.unexpected', 'Error inesperado al facturar las prefacturas. Inténtalo de nuevo.'),
        variant: 'destructive',
      });
    } finally {
      setIsInvoicing(false);
    }
  };

  return {
    bulkInvoiceViewPreInvoices,
    isInvoicing,
  };
};

